import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const STATUSES = [
  { id: 'not_yet', label: 'Not yet contacted', color: '#888' },
  { id: 'in_progress', label: 'In progress', color: '#C9A84C' },
  { id: 'activated', label: 'Activated ✓', color: '#00712D' },
]

export default function HotTen({ session, onStatsUpdate }) {
  const [contacts, setContacts] = useState([])
  const [showAdd, setShowAdd] = useState(false)
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState({ name: '', company: '', title: '', relationship: '', contact_method: 'Phone' })
  const [generatingFor, setGeneratingFor] = useState(null)
  const [generatedMsg, setGeneratedMsg] = useState('')

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const { data } = await supabase.from('hot_ten').select('*').eq('user_id', session.user.id).order('created_at')
    setContacts(data || [])
    setLoading(false)
  }

  async function add() {
    if (!form.name.trim()) return
    await supabase.from('hot_ten').insert({ user_id: session.user.id, ...form, status: 'not_yet' })
    setForm({ name: '', company: '', title: '', relationship: '', contact_method: 'Phone' })
    setShowAdd(false)
    load()
    if (onStatsUpdate) onStatsUpdate()
  }

  async function updateStatus(id, status) {
    await supabase.from('hot_ten').update({ status, last_contacted_at: new Date().toISOString() }).eq('id', id)
    load()
    if (onStatsUpdate) onStatsUpdate()
  }

  async function saveNote(id, note) {
    setContacts(prev => prev.map(c => c.id === id ? { ...c, notes: note } : c))
    await supabase.from('hot_ten').update({ notes: note }).eq('id', id)
  }

  async function remove(id) {
    await supabase.from('hot_ten').delete().eq('id', id)
    load()
    if (onStatsUpdate) onStatsUpdate()
  }

  function generateMessage(contact) {
    setGeneratingFor(contact.id)
    setGeneratedMsg(`Hi ${contact.name.split(' ')[0]},

[Start with genuine connection — ask how they are, how their family is doing, what's new. Spend real time here. This is not a tactic. These are people you actually care about.]

Then, when the moment feels right:

"Things are fine, but honestly I've been thinking about what's next for me. I'm starting to have some conversations and I wanted to reach out to the people I trust most. You know my work better than almost anyone."

Then make the specific ask:

"I'm looking for a [your target title] role [at companies like X, Y, Z / in the [industry] space]. Do you know anyone I should be talking to?"

If they work at one of your target companies:

"I actually saw that [their company] has a [specific role] open right now that looks like a strong fit. Would you be comfortable referring me internally? I can send you my resume right now while we're on the phone."

Make it easy for them to help:

"All you'd need to do is send a quick LinkedIn message introducing us. I can even draft it for you — just two or three sentences you can copy or edit however you like."

Then follow up within 24 hours with everything they need: the LinkedIn profile of the person they're introducing you to, two or three sentences about you they can forward, and your resume attached.

Note: Edit this to sound exactly like you before the call. The four parts are: genuine connection, honest disclosure, specific ask, make it easy to help.`)
  }

  const activated = contacts.filter(c => c.status === 'activated').length

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <div>
          <h2 className="section-title" style={{ margin: 0 }}>Hot Ten</h2>
          <p style={{ color: '#888', fontSize: '0.78rem', marginTop: '0.15rem' }}>{activated} of 10 activated · {10 - contacts.length} spots remaining</p>
        </div>
        {contacts.length < 10 && <button className="btn-primary" onClick={() => setShowAdd(!showAdd)} style={{ padding: '0.55rem 1rem', fontSize: '0.82rem' }}>+ Add</button>}
      </div>

      <div style={{ height: '5px', background: '#e8e8e8', borderRadius: '3px', marginBottom: '1rem', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${(contacts.length / 10) * 100}%`, background: '#00712D', transition: 'width 0.3s' }} />
      </div>

      <div style={{ background: '#E8F2EC', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.78rem', color: '#1A1A1A', lineHeight: 1.6 }}>
        <strong>The criteria:</strong> They know your work, they want you to succeed, and they have professional reach to open doors. Former managers, mentors, respected colleagues, clients who valued your work.
      </div>

      {showAdd && (
        <div className="card" style={{ marginBottom: '1rem', borderTop: '3px solid #00712D' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <input className="input-field" placeholder="Full name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={{ fontSize: '0.875rem' }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <input className="input-field" placeholder="Company" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} style={{ fontSize: '0.875rem' }} />
              <input className="input-field" placeholder="Their title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} style={{ fontSize: '0.875rem' }} />
            </div>
            <input className="input-field" placeholder="Your relationship (e.g. my manager at Acme 2021-2023)" value={form.relationship} onChange={e => setForm({ ...form, relationship: e.target.value })} style={{ fontSize: '0.875rem' }} />
            <select className="input-field" value={form.contact_method} onChange={e => setForm({ ...form, contact_method: e.target.value })} style={{ fontSize: '0.875rem' }}>
              {['Phone', 'LinkedIn', 'Email', 'Text'].map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn-primary" onClick={add}>Add to Hot Ten</button>
            <button className="btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>Loading...</div>}

      {!loading && contacts.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>👥</div>
          <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Your Hot Ten is empty</div>
          <p style={{ fontSize: '0.82rem' }}>Day 5 walks you through building it. Add your first contact above.</p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {contacts.map((contact, idx) => {
          const statusObj = STATUSES.find(s => s.id === contact.status) || STATUSES[0]
          return (
            <div key={contact.id} className="card" style={{ padding: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.65rem' }}>
                <div style={{ display: 'flex', gap: '0.65rem', alignItems: 'center' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#E8F2EC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Georgia,serif', fontWeight: 700, color: '#00712D', fontSize: '0.85rem', flexShrink: 0 }}>{idx + 1}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{contact.name}</div>
                    <div style={{ color: '#555', fontSize: '0.75rem' }}>{contact.title}{contact.company ? ` · ${contact.company}` : ''}</div>
                    {contact.relationship && <div style={{ color: '#aaa', fontSize: '0.7rem' }}>{contact.relationship}</div>}
                  </div>
                </div>
                <span style={{ background: statusObj.color + '20', color: statusObj.color, padding: '0.18rem 0.5rem', borderRadius: '20px', fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{statusObj.label}</span>
              </div>

              {contact.last_contacted_at && (
                <div style={{ fontSize: '0.68rem', color: '#aaa', marginBottom: '0.5rem' }}>
                  Last contacted {new Date(contact.last_contacted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                {STATUSES.map(s => (
                  <button key={s.id} onClick={() => updateStatus(contact.id, s.id)} style={{ padding: '0.25rem 0.6rem', border: `1px solid ${contact.status === s.id ? s.color : '#e8e8e8'}`, borderRadius: '20px', background: contact.status === s.id ? s.color + '15' : '#fff', color: contact.status === s.id ? s.color : '#888', fontSize: '0.7rem', fontWeight: contact.status === s.id ? 700 : 400, cursor: 'pointer' }}>
                    {s.label}
                  </button>
                ))}
                <button onClick={() => generateMessage(contact)} style={{ padding: '0.25rem 0.6rem', border: '1px solid #e8e8e8', borderRadius: '20px', background: '#fff', color: '#2563eb', fontSize: '0.7rem', cursor: 'pointer' }}>Generate message</button>
                <button onClick={() => remove(contact.id)} style={{ padding: '0.25rem 0.6rem', border: '1px solid #fee2e2', borderRadius: '20px', background: '#fff', color: '#dc2626', fontSize: '0.7rem', cursor: 'pointer' }}>Remove</button>
              </div>

              {generatingFor === contact.id && generatedMsg && (
                <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#f9f9f9', borderRadius: '6px', border: '1px solid #e8e8e8' }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>Edit before sending</div>
                  <textarea value={generatedMsg} onChange={e => setGeneratedMsg(e.target.value)} rows={7} style={{ width: '100%', border: 'none', background: 'transparent', resize: 'vertical', fontSize: '0.8rem', lineHeight: 1.65, fontFamily: 'inherit', outline: 'none' }} />
                  <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem' }}>
                    <button onClick={() => navigator.clipboard.writeText(generatedMsg)} className="btn-primary" style={{ padding: '0.4rem 0.85rem', fontSize: '0.75rem' }}>Copy</button>
                    <button className="btn-ghost" onClick={() => { setGeneratingFor(null); setGeneratedMsg('') }} style={{ fontSize: '0.75rem' }}>Close</button>
                  </div>
                </div>
              )}

              <div style={{ marginTop: '0.65rem' }}>
                <textarea
                  defaultValue={contact.notes || ''}
                  onBlur={e => saveNote(contact.id, e.target.value)}
                  placeholder="What did they say? Any leads, intros, or follow-ups to remember..."
                  rows={2}
                  style={{ width: '100%', border: '1px solid #f0f0f0', borderRadius: '5px', padding: '0.5rem 0.65rem', fontSize: '0.78rem', color: '#1A1A1A', fontFamily: 'inherit', lineHeight: 1.5, resize: 'vertical', outline: 'none', background: '#fafafa' }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
